import { useQuery } from "@tanstack/react-query";
import { userService } from "@/services/breeze/UserService";
import { useAuth } from "@/providers/AuthProvider";
import { SkeletonPage } from "@/components/ui/Skeleton";
import { Unauthorized } from "@/components/ui/Unauthorized";

const withTenantAdminRequired = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
) => {
  const ProtectedComponent = (props: P) => {
    const { currentTenant, loading: authLoading } = useAuth();

    // Gated on authLoading: a backgrounded tab briefly sees currentTenant=null and would cache a false negative.
    const { data: isAuthorized, isPending, isError } = useQuery({
      queryKey: ["my-is-admin", currentTenant?.id],
      queryFn: () => userService.checkMyUserIsAdmin(),
      enabled: !authLoading && !!currentTenant?.id,
    });

    if (authLoading) return <SkeletonPage />;

    if (!currentTenant?.id) {
      return (
        <Unauthorized message="Your session may have expired. Refresh the page or sign in again." />
      );
    }

    if (isPending) return <SkeletonPage />;

    if (isError) {
      return (
        <Unauthorized message="We couldn't verify your access. Refresh the page and try again." />
      );
    }

    return isAuthorized ? (
      <WrappedComponent {...props} />
    ) : (
      <Unauthorized message="You need administrator permissions to access this page." />
    );
  };

  return ProtectedComponent;
};

export default withTenantAdminRequired;
